import React, { useState } from "react";
import {
  RentControlTypesFormRentControlType,
  DefaultsPageConfiguration
} from "api";
import createDataTable, {
  IPagedTableState,
  ColumnDefinition,
  DataTableProps
} from "components/controls/createTable";

import Input from "components/controls/Input";
import listUtils from "utils/listUtils";

export interface RentControlTypesFormTableProps {
  rentControlTypes?: RentControlTypesFormRentControlType[] | undefined;
  onChange: (rentControlTypes: RentControlTypesFormRentControlType[]) => void;
  onRemoveRowAction?:
    | ((rowToRemove: RentControlTypesFormRentControlType) => void)
    | undefined;
  onAddRowAction?: (() => void) | undefined;
  /**
   * Limits which rows can be removed. When omitted every row can be removed.
   */
  canRemoveRow?:
    | ((row: RentControlTypesFormRentControlType) => boolean)
    | undefined;
  pageConfiguration: DefaultsPageConfiguration | undefined;
}

interface TableState extends IPagedTableState {}

const RentControlTypesFormTable: React.FC<RentControlTypesFormTableProps> =
  function (props: RentControlTypesFormTableProps) {
    const [table, setTable] = useState<TableState>({
      offset: 0,
      limit: 10
    });

    const isEditEnabled = !!(
      props.pageConfiguration && props.pageConfiguration.isEditEnabled
    );

    const norm = (value: string | null | undefined) => value || undefined;

    const onRowChanged = (
      oldRow: RentControlTypesFormRentControlType,
      newRow: RentControlTypesFormRentControlType
    ) => {
      if (!props.rentControlTypes) {
        return;
      }

      if (norm(oldRow.displayValue) === norm(newRow.displayValue)) {
        return;
      }

      newRow.isChanged = true;

      props.onChange(
        listUtils.copyListWithNewItem(props.rentControlTypes, oldRow, newRow)
      );
    };

    const getNameBody = function (rowData: any) {
      const rentControlType = rowData as RentControlTypesFormRentControlType;

      if (!isEditEnabled) {
        return <div>{rentControlType.displayValue}</div>;
      }

      return (
        <Input
          value={rentControlType.displayValue}
          validationMessage={
            rentControlType.fieldValidationErrors &&
            rentControlType.fieldValidationErrors["DisplayValue"]
          }
          onUnfocus={(value: string) => {
            onRowChanged(rentControlType, {
              ...rentControlType,
              displayValue: value || undefined
            });
          }}
        />
      );
    };

    const getColumns = function (): any[] {
      const columns = [] as ColumnDefinition<
        RentControlTypesFormRentControlType,
        keyof RentControlTypesFormRentControlType
      >[];

      return columns.concat([
        {
          field: "displayValue",
          header: "Rent control",
          style: { width: "40%" },
          body: getNameBody
        },
        {
          field: "lastUpdatedDate",
          header: "Last updated date",
          style: { width: "25%", textAlign: "center" }
        },
        {
          field: "lastUpdatedBy",
          header: "Last updated by",
          style: { width: "25%", textAlign: "center" }
        }
      ]);
    };

    const tableProps: DataTableProps<
      RentControlTypesFormRentControlType,
      TableState
    > = {
      value: props.rentControlTypes || undefined,
      editMode: "cell",
      state: table,
      updateState: setTable,
      onAddRowAction: props.onAddRowAction,
      onRemoveRowAction: isEditEnabled ? props.onRemoveRowAction : undefined,
      canRemoveRow: props.canRemoveRow,
      emptyMessage: "No rent control types have been added."
    };

    return createDataTable<RentControlTypesFormRentControlType, TableState>(
      getColumns(),
      tableProps
    );
  };

export default RentControlTypesFormTable;
